import type { ReactNode } from "react";
import { useMutation } from "@connectrpc/connect-query";
import { Link, Outlet, createFileRoute } from "@tanstack/react-router";

import { AuthService, type StaffRole, type StaffSession } from "@yatayat/gen/yatayat/auth/v1/auth_pb";

import { StaffSignIn } from "~/components/StaffSignIn";
import { loadStaffMessages, useI18n } from "~/lib/i18n";
import { staffRoleLabels } from "~/lib/labels";
import { can, useStaffSession } from "~/lib/staff";
import { useRefreshStaffSession } from "~/lib/staff-refresh";
import { ui } from "~/lib/ui";

export const Route = createFileRoute("/operator")({
  loader: () => loadStaffMessages(),
  component: OperatorLayout,
});

/** Gates every operator page behind a staff session whose role may use the operator app. */
function OperatorLayout() {
  const { t } = useI18n();
  const staff = useStaffSession();
  const session = staff.session;

  if (!session) {
    let body: ReactNode;
    if (staff.isPending) {
      body = <p className="text-slate-600 dark:text-slate-400">{t("loading")}</p>;
    } else if (staff.isError) {
      body = <p className="text-brand dark:text-brand-dark">{t("somethingWrong")}</p>;
    } else {
      body = (
        <>
          <p className="text-slate-600 dark:text-slate-400">{t("operatorBody")}</p>
          <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
            <StaffSignIn showRole={can.useOperatorApp} />
          </div>
        </>
      );
    }

    return (
      <div className="mx-auto max-w-md space-y-4">
        <h1 className={ui.h1}>{t("operatorTitle")}</h1>
        {body}
      </div>
    );
  }

  if (!can.useOperatorApp(session.role)) {
    return (
      <div className="mx-auto max-w-md space-y-4">
        <h1 className={ui.h1}>{t("operatorTitle")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("staffNoOperatorApp")}</p>
        <StaffSignOutButton />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <StaffBar session={session} />
        <OperatorNav role={session.role} />
      </div>
      <Outlet />
    </div>
  );
}

const tab = "-mb-px rounded-t-md border-b-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-900";
const tabActive = { className: "border-ink font-semibold text-ink dark:border-ink-dark dark:text-ink-dark" };
const tabInactive = { className: "border-transparent text-slate-600 dark:text-slate-400" };

/** The operator app's sections a role may open; none for a role with only Trips. */
function OperatorNav({ role }: { role: StaffRole }) {
  const { t } = useI18n();
  const schedules = can.manage(role);
  const report = can.viewReport(role);
  const stickers = can.printStickers(role);

  if (!schedules && !report && !stickers) return null;

  return (
    <nav aria-label={t("operatorNavLabel")} className="flex flex-wrap gap-1 border-b border-slate-200 text-sm dark:border-slate-800">
      {/* Exact, so Trips isn't also marked current on the other sections. */}
      <Link
        to="/operator"
        activeOptions={{ exact: true, includeSearch: false }}
        className={tab}
        activeProps={tabActive}
        inactiveProps={tabInactive}
      >
        {t("navTrips")}
      </Link>
      {schedules && (
        <Link to="/operator/schedules" className={tab} activeProps={tabActive} inactiveProps={tabInactive}>
          {t("navSchedules")}
        </Link>
      )}
      {report && (
        <Link to="/operator/report" activeOptions={{ includeSearch: false }} className={tab} activeProps={tabActive} inactiveProps={tabInactive}>
          {t("navReport")}
        </Link>
      )}
      {stickers && (
        <Link to="/operator/stickers" className={tab} activeProps={tabActive} inactiveProps={tabInactive}>
          {t("navStickers")}
        </Link>
      )}
    </nav>
  );
}

function StaffBar({ session }: { session: StaffSession }) {
  const { t, lang } = useI18n();

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
      <div className="mr-auto min-w-0">
        <Link to="/operator" className="font-semibold text-ink underline-offset-2 hover:underline dark:text-ink-dark">
          {session.operatorName?.[lang] || t("operatorTitle")}
        </Link>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {session.name} · {t(staffRoleLabels[session.role])}
        </p>
      </div>
      <StaffSignOutButton />
    </div>
  );
}

function StaffSignOutButton() {
  const { t } = useI18n();
  const refreshStaffSession = useRefreshStaffSession();
  // Stay pending until the session is refetched, so the page doesn't flash.
  const signOut = useMutation(AuthService.method.staffSignOut, { onSuccess: () => refreshStaffSession() });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        disabled={signOut.isPending}
        onClick={() => signOut.mutate({})}
        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-900"
      >
        {t("staffSignOut")}
      </button>
      {signOut.isError && (
        <p role="alert" className="text-sm text-brand dark:text-brand-dark">
          {t("somethingWrong")}
        </p>
      )}
    </div>
  );
}
